import { CancelationReasonCell } from '@exam-center/exam-event-examinees';
import {
  ExamEventExamineeCertificateStatus,
  type SearchExamineeStatusesInExamEventQuery,
  useSearchExamineeStatusesInExamEventQuery,
} from '@exam-center/graphql';
import { useParams } from '@exam-center/router';
import { EllipsisText, Text } from '@shared/components';
import { type TableBodyRow } from '@shared/table-generator';
import { formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

import { ExamineeMonitorTable, ExamineeProgressIcon } from '../../components';
import { ExamineeMonitorEkycIcon } from '../../components/examinee-monitor-ekyc-icon';
import {
  EXAM_EVENT_EXAMINEE_STATUS_LABEL,
  ExamineeMonitorStatusLabel,
} from '../../components/examinee-monitor-status-label';
import { headersWithoutStatuses } from '../../components/examinee-monitor-table/columns';
import { useExamEventSessionExamineeSearchParams } from '../../hooks';
import { ExamineeMonitorMoreActionMenu } from '../examinee-monitor-more-action-menu';

function generateRows(
  rows: SearchExamineeStatusesInExamEventQuery['ecSearchExamineeStatusesInExamEvent']['examEventExaminees'],
): TableBodyRow[] {
  return rows.map((examEventExaminee) => ({
    children: [
      { id: 'seatNumber', children: examEventExaminee.seatNumber?.toString() ?? '-' },
      {
        id: 'name',
        children: `${examEventExaminee.examinee.title} ${examEventExaminee.examinee.firstName} ${examEventExaminee.examinee.lastName}`,
      },
      { id: 'idNumber', children: examEventExaminee.examinee.idNumber },
      { id: 'email', children: <EllipsisText>{examEventExaminee.email}</EllipsisText> },
      {
        id: 'ekycStatus',
        children: <ExamineeMonitorEkycIcon status={examEventExaminee.examinee.eKycStatus} />,
      },
      {
        id: 'isExamReady',
        children: (
          <ExamineeProgressIcon
            variant={examEventExaminee.isExamReady ? 'completed' : 'disabled'}
          />
        ),
      },
      { id: 'course', children: examEventExaminee.examCourse.code },
      { id: 'status', children: EXAM_EVENT_EXAMINEE_STATUS_LABEL[examEventExaminee.status] },
      {
        id: 'remark',
        children: (
          <CancelationReasonCell
            cancelationReasonCategory={examEventExaminee.cancelationReason}
            remark={examEventExaminee.remark}
          />
        ),
      },
      {
        id: 'result',
        children: examEventExaminee.examEventExamineeResult?.result ? (
          <ExamineeMonitorStatusLabel status={examEventExaminee.examEventExamineeResult?.result} />
        ) : (
          <Text sx={{ textAlign: 'center' }}>-</Text>
        ),
      },
      {
        id: 'certificate',
        children: (
          <ExamineeProgressIcon
            variant={
              examEventExaminee.certificateStatus === ExamEventExamineeCertificateStatus.Sent
                ? 'completed'
                : 'disabled'
            }
          />
        ),
      },
      {
        id: 'sentToSETPortalAt',
        children: examEventExaminee.sentToSETPortalAt
          ? formatDateTime(examEventExaminee.sentToSETPortalAt)
          : '-',
      },
      {
        id: 'action',
        sx: (theme) => ({ padding: theme.spacing(1.5) }),
        children: (
          <ExamineeMonitorMoreActionMenu
            examEventExamineeId={examEventExaminee.id}
            examEventExamineeSeatNumber={examEventExaminee.seatNumber}
            examEventExamineeEkycStatus={examEventExaminee.examinee.eKycStatus}
            examEventExamineeStatus={examEventExaminee.status}
            certificateStatus={examEventExaminee.certificateStatus}
            examCourseCode={examEventExaminee.examCourse.code}
            examEventExamineeRegistrationStatus={examEventExaminee.registrationStatus}
            examEventSessionExamineesExamStartedAt={
              examEventExaminee.examEventSessionExaminees?.map((s) => s.examStartedAt) ?? []
            }
          />
        ),
      },
    ],
  }));
}

export default function ExamineeMonitorListWithoutStatuses() {
  const { examEventId } = useParams('/:examCenterCode/proctoring/exam-events/:examEventId/details');
  const [{ search }] = useExamEventSessionExamineeSearchParams();
  const { data, loading: isLoading } = useSearchExamineeStatusesInExamEventQuery({
    variables: {
      searchExamineeStatusesInExamEvent: {
        examEventId: examEventId,
        searchExaminee: {
          filter: {
            searchText: {
              fields: ['fullName'],
              value: search ?? '',
            },
          },
        },
        paging: null,
      },
    },
    pollInterval: 10000,
  });

  const examEventExaminees = useMemo(
    () => data?.ecSearchExamineeStatusesInExamEvent.examEventExaminees ?? [],
    [data?.ecSearchExamineeStatusesInExamEvent],
  );

  const rows = useMemo(() => generateRows(examEventExaminees), [examEventExaminees]);

  return (
    <ExamineeMonitorTable
      rows={rows}
      columns={headersWithoutStatuses}
      isLoading={isLoading}
      slotProps={{
        tableHeaderCell: {
          sx: (theme) => ({
            p: theme.spacing(3),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.grey[100],
            ...theme.typography.body1Semibold,
          }),
        },
        tableBodyRow: {
          sx: {
            '&:last-child td': {
              borderBottom: 'none',
            },
          },
        },
        tableBodyCell: {
          sx: (theme) => ({
            p: theme.spacing(2, 3),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            ...theme.typography.body1,
            '&:last-child': {
              borderRight: 'none',
            },
            '&:nth-of-type(1)': {
              textAlign: 'center',
            },
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }),
        },
      }}
    />
  );
}
